import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { adminEmailTemplateService, AdminEmailTemplate } from '../../services/adminEmailTemplateService';

const AdminTemplateManager: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [templates, setTemplates] = useState<AdminEmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const allTemplates = await adminEmailTemplateService.getAllTemplates();
      setTemplates(allTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      showError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId: string, templateName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${templateName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await adminEmailTemplateService.deleteTemplate(templateId);
      showSuccess('Template deleted successfully');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showError('Failed to delete template');
    }
  };

  const handlePublish = async (templateId: string) => {
    if (!currentUser) return;

    try {
      await adminEmailTemplateService.publishTemplate(templateId, currentUser.uid);
      showSuccess('Template published successfully');
      loadTemplates();
    } catch (error) {
      console.error('Error publishing template:', error);
      showError('Failed to publish template');
    }
  };

  const handleUnpublish = async (templateId: string) => {
    if (!currentUser) return;

    try {
      await adminEmailTemplateService.unpublishTemplate(templateId, currentUser.uid);
      showSuccess('Template unpublished');
      loadTemplates();
    } catch (error) {
      console.error('Error unpublishing template:', error);
      showError('Failed to unpublish template');
    }
  };

  const handleToggleFeatured = async (templateId: string, currentFeatured: boolean) => {
    if (!currentUser) return;

    try {
      await adminEmailTemplateService.setFeatured(templateId, !currentFeatured, currentUser.uid);
      showSuccess(`Template ${!currentFeatured ? 'featured' : 'unfeatured'}`);
      loadTemplates();
    } catch (error) {
      console.error('Error toggling featured status:', error);
      showError('Failed to update featured status');
    }
  };

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    // Status filter
    if (filterStatus === 'published' && !template.isPublished) return false;
    if (filterStatus === 'draft' && template.isPublished) return false;

    // Category filter
    if (filterCategory !== 'all' && template.category !== filterCategory) return false;

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        template.name.toLowerCase().includes(searchLower) ||
        template.description.toLowerCase().includes(searchLower) ||
        template.tags.some(tag => tag.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }

    return true;
  });

  const categories = [
    'arts-entertainment',
    'blogs-updates',
    'business',
    'deals-offers',
    'ecommerce',
    'events',
    'festive-season',
    'health-wellness',
    'newsletter',
    'onboarding',
    'professional-services',
    'promotional'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Email Templates</h1>
              <p className="text-slate-600 mt-1">Manage and publish email templates for users</p>
            </div>
            <button
              onClick={() => navigate('/templates/new')}
              className="px-4 py-2 bg-ocean-500 text-white rounded-lg hover:bg-ocean-600 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Create New Template</span>
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-lg border border-slate-200">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-slate-700">Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-slate-700">Category:</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => {
                  const categoryLabels: { [key: string]: string } = {
                    'arts-entertainment': 'Arts & Entertainment',
                    'blogs-updates': 'Blogs & Updates',
                    'business': 'Business',
                    'deals-offers': 'Deals & Offers',
                    'ecommerce': 'E-commerce',
                    'events': 'Events',
                    'festive-season': 'Festive & Season',
                    'health-wellness': 'Health & Wellness',
                    'newsletter': 'Newsletter',
                    'onboarding': 'Onboarding',
                    'professional-services': 'Professional Services',
                    'promotional': 'Promotional'
                  };
                  return (
                    <option key={cat} value={cat}>
                      {categoryLabels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search templates..."
                className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
              />
            </div>

            <div className="text-sm text-slate-600">
              {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <svg className="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No templates found</h3>
            <p className="text-slate-600 mb-4">
              {searchTerm || filterStatus !== 'all' || filterCategory !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating your first template'}
            </p>
            {!searchTerm && filterStatus === 'all' && filterCategory === 'all' && (
              <button
                onClick={() => navigate('/templates/new')}
                className="px-4 py-2 bg-ocean-500 text-white rounded-lg hover:bg-ocean-600 transition-colors"
              >
                Create Template
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Preview Image */}
                <div className="h-48 bg-slate-100 relative">
                  {template.previewImage ? (
                    <img
                      src={template.previewImage}
                      alt={template.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  )}
                  {template.isFeatured && (
                    <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded text-xs font-semibold">
                      ⭐ Featured
                    </div>
                  )}
                  <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-semibold ${
                    template.isPublished 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {template.isPublished ? 'Published' : 'Draft'}
                  </div>
                </div>

                {/* Template Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-1 truncate">{template.name}</h3>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{template.description}</p>
                  
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                    <span className="capitalize">{template.category}</span>
                    <span>{template.usageCount} uses</span>
                  </div>

                  {/* Tags */}
                  {template.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {template.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                      {template.tags.length > 3 && (
                        <span className="px-2 py-0.5 text-slate-400 text-xs">+{template.tags.length - 3}</span>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center space-x-2 pt-3 border-t border-slate-200">
                    <button
                      onClick={() => navigate(`/templates/${template.id}/edit`)}
                      className="flex-1 px-3 py-1.5 bg-ocean-500 text-white text-sm rounded hover:bg-ocean-600 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (template.isPublished) {
                          handleUnpublish(template.id!);
                        } else {
                          handlePublish(template.id!);
                        }
                      }}
                      className="px-3 py-1.5 bg-slate-200 text-slate-700 text-sm rounded hover:bg-slate-300 transition-colors"
                    >
                      {template.isPublished ? 'Unpublish' : 'Publish'}
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => {
                          // Toggle dropdown menu
                        }}
                        className="p-1.5 text-slate-600 hover:bg-slate-100 rounded transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTemplateManager;


